import type {
    MultichatActivePromo,
    MultichatBootstrapChannel,
    MultichatPendingPromo,
} from '@/helpers/gcb2/multichat/types'
import type { MultichatSlotLike } from '@/helpers/gcb2/multichat/resolveChannels'
import { SMS_CHANNEL_SLOT_TYPE, isSmsChannelSlotType } from '@/helpers/channels/channelSlots'
import { getMultichatChannelDef } from '@/helpers/gcb2/multichat/channelDefs'
import { isSmsChannelIntegrationId } from '@/helpers/promo/integrations'
import { resolveSmsOfferKind } from '@/helpers/promo/availableActivationTypes'

export { SMS_CHANNEL_SLOT_TYPE }

export function findFreeSmsSlotId(slots: MultichatSlotLike[] | null | undefined): string | null {
    const list = slots || []
    const free = list.find(
        (slot) => isSmsChannelSlotType(slot) && slot?.channelConnectorId == null && slot?.id != null
    )
    return free?.id != null ? String(free.id) : null
}

export type SmsPromoDockTone = 'default' | 'active' | 'paid' | 'expiring' | 'expired'
export type SmsPromoDockAction =
    | 'activate-gift'
    | 'open-crm-offer'
    | 'start-sms'
    | 'renew'
    | 'renew-again'

export type SmsPromoDockRow = {
    kind: 'gift' | 'discount'
    tone: SmsPromoDockTone
    title: string
    sub: string
    chip: string | null
    primaryLabel?: string
    primaryAction?: SmsPromoDockAction
    primaryDisabled?: boolean
    secondaryLabel?: string
    secondaryAction?: SmsPromoDockAction
}

function dayWord(days: number): string {
    const n = Math.abs(days) % 100
    const n1 = n % 10
    if (n > 10 && n < 20) return 'дней'
    if (n1 === 1) return 'день'
    if (n1 >= 2 && n1 <= 4) return 'дня'
    return 'дней'
}

export function smsRemainingLabel(days: number | null): string {
    if (days == null || days <= 0) return 'Срок закончился'
    const verb = days === 1 ? 'Остался' : 'Осталось'
    return `${verb} ${days} ${dayWord(days)}`
}

export function findActiveSmsPromo(
    promos: MultichatActivePromo[] | null | undefined
): MultichatActivePromo | null {
    if (!promos?.length) return null
    return promos.find((p) => isSmsChannelIntegrationId(p.integrationId)) || null
}

export function isSmsDiscountActivation(activationType: string | null | undefined): boolean {
    return String(activationType || '').trim().toLowerCase() === 'discount'
}

export function resolveSmsPromoDockRow(options: {
    pendingSms: MultichatPendingPromo | null
    activeSms: MultichatActivePromo | null
    smsExists: boolean
    smsConnected: boolean
}): SmsPromoDockRow | null {
    const pending = options.pendingSms
    const active = options.activeSms?.relevance === 'already_installed' ? null : options.activeSms

    if (active && isSmsDiscountActivation(active.activationType)) {
        return {
            kind: 'discount',
            tone: 'paid',
            title: 'Скидка 10% на внедрение CRM',
            sub: 'Скидка получена.',
            chip: null,
        }
    }

    if (pending) {
        if (pending.status === 'activating') {
            return {
                kind: 'gift',
                tone: 'default',
                title: 'Подключаем SMS DH',
                sub: 'Активация уже запущена.',
                chip: null,
                primaryLabel: 'Подключаем…',
                primaryAction: 'activate-gift',
                primaryDisabled: true,
            }
        }
        const offer = resolveSmsOfferKind(pending.availableActivationTypes, options.smsExists)
        if (offer === 'discount') {
            return {
                kind: 'discount',
                tone: 'default',
                title: 'SMS подключён — вам скидка 10%',
                sub: 'На внедрение CRM-маркетинга. Приглашаем на диагностику.',
                chip: null,
                primaryLabel: 'Получить скидку',
                primaryAction: 'open-crm-offer',
            }
        }
        if (offer === 'integration') {
            return {
                kind: 'gift',
                tone: 'default',
                title: 'Ваш подарок ещё доступен',
                sub: 'Дополнительный канал SMS DH на 30 дней сохранён для проекта.',
                chip: null,
                primaryLabel: 'Подключить бесплатно',
                primaryAction: 'activate-gift',
            }
        }
    }

    if (!active) return null

    const daysLeft = active.daysLeft
    const periodChip = daysLeft == null ? null : smsRemainingLabel(daysLeft)
    const expired = active.status === 'expired' || (daysLeft != null && daysLeft <= 0)
    if (expired) {
        return {
            kind: 'gift',
            tone: 'expired',
            title: 'SMS DH приостановлен',
            sub: 'Подключение и история переписок сохранены. Продлите канал, чтобы снова отправлять SMS.',
            chip: 'Срок закончился',
            primaryLabel: 'Продлить',
            primaryAction: 'renew',
        }
    }

    const expiring = active.status === 'need_prolong' || (daysLeft != null && daysLeft <= 3)
    if (expiring) {
        return {
            kind: 'gift',
            tone: 'expiring',
            title: 'Подарок SMS DH скоро закончится',
            sub: 'Телефон онлайн. Продлите канал, чтобы сообщения продолжили отправляться.',
            chip: periodChip,
            primaryLabel: 'Продлить',
            primaryAction: 'renew',
        }
    }

    if (options.smsConnected) {
        return {
            kind: 'gift',
            tone: 'paid',
            title: 'SMS DH работает',
            sub: 'Рабочий телефон онлайн, сообщения отправляются с SIM‑карты компании.',
            chip: periodChip,
            primaryLabel: 'Продлить',
            primaryAction: 'renew',
        }
    }

    return {
        kind: 'gift',
        tone: 'active',
        title: 'SMS DH активирован',
        sub: 'Осталось установить приложение и подключить рабочий телефон.',
        chip: periodChip,
        primaryLabel: 'Настроить SMS DH',
        primaryAction: 'start-sms',
        secondaryLabel: 'Продлить',
        secondaryAction: 'renew',
    }
}

export type SmsOnboardingAction = 'open-sms-promo' | 'open-crm-offer' | 'start-sms' | 'renew'

export type SmsOnboardingRow = {
    tone: SmsPromoDockTone
    description: string
    status: string
    button?: string
    buttonClass?: 'primary' | 'secondary'
    action?: SmsOnboardingAction
}

export function buildSmsBootstrapChannel(options: {
    exists: boolean
    connected: boolean
    connectorId?: string | null
}): MultichatBootstrapChannel {
    const def = getMultichatChannelDef('sms')
    const status = options.connected ? 'ready' : options.exists ? 'connect' : 'not_entitled'
    return {
        key: 'sms',
        connectorType: def?.connectorType || 'smsChannel',
        providerType: def?.dhProviderType || 'smsGate',
        entitled: options.exists || options.connected,
        paid: options.exists || options.connected,
        provider: options.exists || options.connected ? 'dh' : null,
        status,
        displayName: def?.displayName || 'SMS DH',
        connectorId: options.connectorId || null,
    }
}

export function resolveSmsOnboardingRow(options: {
    pendingSms: MultichatPendingPromo | null
    activeSms: MultichatActivePromo | null
    smsExists: boolean
    smsConnected: boolean
    /** Phone online and SIM number saved. Absent means not ready to send. */
    smsReady?: boolean
}): SmsOnboardingRow | null {
    const pending = options.pendingSms
    const active = options.activeSms?.relevance === 'already_installed' ? null : options.activeSms
    const giftActive = Boolean(active && !isSmsDiscountActivation(active.activationType))
    const daysLeft = giftActive ? active?.daysLeft ?? null : null
    const period = daysLeft == null ? null : smsRemainingLabel(daysLeft)
    const expired = Boolean(
        giftActive && (active?.status === 'expired' || (daysLeft != null && daysLeft <= 0))
    )
    const expiring = Boolean(
        giftActive &&
            (active?.status === 'need_prolong' || (daysLeft != null && daysLeft <= 3))
    )

    if (expired) {
        return {
            tone: 'expired',
            description: 'Продлите канал для отправки SMS',
            status: 'Приостановлен',
            button: 'Продлить',
            buttonClass: 'primary',
            action: 'renew',
        }
    }

    if (expiring && options.smsConnected) {
        return {
            tone: 'expiring',
            description:
                'Телефон онлайн, SMS отправляются. Продлите канал, чтобы сохранить работу после подарочного периода.',
            status: period ? `Подарок · ${period.toLowerCase()}` : 'Подарок скоро закончится',
            button: 'Продлить',
            buttonClass: 'primary',
            action: 'renew',
        }
    }

    if (options.smsConnected) {
        if (!options.smsReady) {
            return {
                tone: 'paid',
                description: 'SMS с рабочего номера',
                status: 'Подключён',
                button: 'Настроить',
                buttonClass: 'primary',
                action: 'start-sms',
            }
        }
        return {
            tone: 'paid',
            description: 'SMS с рабочего номера',
            status: 'Подключён',
        }
    }

    if (giftActive || options.smsExists) {
        return {
            tone: 'active',
            description: 'SMS с рабочего номера',
            status: 'Настройка не завершена',
            button: 'Продолжить',
            buttonClass: 'primary',
            action: 'start-sms',
        }
    }

    if (pending && pending.status !== 'activating') {
        const offer = resolveSmsOfferKind(pending.availableActivationTypes, options.smsExists)
        if (offer === 'discount') {
            return {
                tone: 'default',
                description: 'SMS с рабочего номера',
                status: 'Скидка 10%',
                button: 'Получить скидку',
                buttonClass: 'primary',
                action: 'open-crm-offer',
            }
        }
        if (offer === 'integration') {
            const giftDays = pending.durationDays || 30
            return {
                tone: 'default',
                description: 'SMS с рабочего номера',
                status: `${giftDays} дней в подарок`,
                button: 'Подключить бесплатно',
                buttonClass: 'primary',
                action: 'open-sms-promo',
            }
        }
    }

    return null
}
